import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Remote, Rpc } from 'accounts'
import { useEffect, useState } from 'react'
import { Button, Frame } from 'regen-ui'
import { useChainId } from 'wagmi'
import * as z from 'zod/mini'

import * as AuthorizedApps from '#/lib/authorized-apps.js'
import { getSupportedChainId } from '#/lib/chain.js'
import { remote } from '#/lib/config.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import { AuthorizeSpend } from '#/routes/remote/-frames/authorize/AuthorizeSpend.js'
import { useDeposit } from '#/routes/remote/-hooks/useDeposit.js'
import * as AuthorizeAccessKey from '#/routes/remote/-lib/AuthorizeAccessKey.js'
import * as RemoteRpc from '#/routes/remote/-lib/RemoteRpc.js'
import * as ShowDeposit from '#/routes/remote/-lib/ShowDeposit.js'

export const Route = createFileRoute('/remote/rpc/wallet_authorizeAccessKey')({
  component: Wrapper,
  validateSearch: (search) =>
    RemoteRpc.validateSearch(search, {
      method: 'wallet_authorizeAccessKey',
      strict: true,
    }),
})

type Search = Remote.validateSearch.ReturnType<'wallet_authorizeAccessKey'>
type Result = Rpc.wallet_authorizeAccessKey.Encoded['returns']
type PendingDeposit = {
  /** Balance-gate result that decided to show this prompt. */
  balanceGate: ShowDeposit.BalanceGate
  /** Chain used for the optional deposit prompt. */
  chainId: number
  /** Completed access-key authorization result waiting on the optional deposit prompt. */
  result: Result
  /** Requested deposit prompt to render. */
  showDeposit: ShowDeposit.Requested
}

function Wrapper() {
  const search = Route.useSearch() as Search
  return <Component key={search.id} />
}

function Component() {
  const { capture } = useAnalytics()
  const search = Route.useSearch() as Search
  const rpc = RemoteRpc.use()
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host
  const originalAuthorizeAccessKey = search._decoded.params[0]
  const [authorizeAccessKey, setAuthorizeAccessKey] = useState(originalAuthorizeAccessKey)
  const [pendingDeposit, setPendingDeposit] = useState<PendingDeposit | undefined>()
  const fallbackChainId = useChainId()
  const queryClient = useQueryClient()
  // Prefer the chain explicitly requested by the dapp; fall back to the wallet's current chain.
  const chainId = authorizeAccessKey.chainId ? Number(authorizeAccessKey.chainId) : fallbackChainId

  AuthorizeAccessKey.useValidate({
    authorizeAccessKey,
    origin: rpc.consumer.origin,
    reject: rpc.reject,
    trusted: RemoteRpc.isTrusted(rpc),
  })

  useEffect(() => {
    capture('access_key_authorize_started')
  }, [capture])

  const confirm = useMutation({
    mutationFn: async () => {
      const { showDeposit, ...key } = authorizeAccessKey
      // Run the passkey ceremony first so the access-key address is known,
      // then upsert before forwarding the cached result. Soft-fail upsert.
      const params = [
        z.encode(Rpc.wallet_authorizeAccessKey.parameters as never, key as never),
      ] as const
      const result = (await remote.provider.request({
        ...search,
        _decoded: { ...search._decoded, params: [key] },
        params,
      } as never)) as Result
      const accessKeyAddress = result.keyAuthorization?.address
      await AuthorizedApps.upsert({
        chainId,
        key: { ...key, address: accessKeyAddress ?? key.address },
        hostname: consumer.host,
      })
      const requestedShowDeposit = showDeposit || undefined
      if (requestedShowDeposit) {
        const chainId_deposit = getSupportedChainId(chainId)
        const gate = await ShowDeposit.checkBalanceGate({
          address: result.rootAddress,
          chainId: chainId_deposit,
          queryClient,
          showDeposit: requestedShowDeposit,
        })
        if (gate.status === 'show') {
          setPendingDeposit({
            balanceGate: gate,
            chainId: chainId_deposit,
            result,
            showDeposit: requestedShowDeposit,
          })
          return
        }
      }
      return rpc.resolve(result)
    },
    onSuccess: () => {
      capture('access_key_authorize_success')
    },
    onError: (error) => {
      capture('access_key_authorize_failed', { error: error.message })
    },
  })

  if (pendingDeposit)
    return (
      <PostAuthorizeDeposit
        balanceGate={pendingDeposit.balanceGate}
        chainId={pendingDeposit.chainId}
        host={host}
        onContinue={() => {
          void rpc.resolve(pendingDeposit.result)
        }}
        result={pendingDeposit.result}
        showDeposit={pendingDeposit.showDeposit}
      />
    )

  return (
    <AuthorizeSpend
      authorizeAccessKey={authorizeAccessKey}
      confirming={confirm.isPending}
      error={confirm.error?.message}
      expiry_original={originalAuthorizeAccessKey.expiry}
      host={host}
      onApprove={() => confirm.mutate()}
      onAuthorizeAccessKeyChange={setAuthorizeAccessKey}
      onReject={() => rpc.reject()}
    />
  )
}

function PostAuthorizeDeposit(props: PostAuthorizeDeposit.Props) {
  const { balanceGate, chainId, host, onContinue, result, showDeposit } = props
  const params = typeof showDeposit === 'object' ? showDeposit : undefined
  const deposit = useDeposit({
    address: result.rootAddress,
    amount: params?.amount,
    chainId,
    onSuccess: onContinue,
    orActions: (
      <Button
        className="w-full"
        onClick={onContinue}
        size="medium"
        type="button"
        variant="secondary"
      >
        Skip and continue
      </Button>
    ),
    successScreen: false,
    token: params?.token,
  })

  if (deposit.frame) return <Frame.Provider onDismiss={onContinue}>{deposit.frame}</Frame.Provider>

  return (
    <Frame
      bodyClassName="gap-2"
      onDismiss={onContinue}
      subtitle={ShowDeposit.getSubtitle({
        amount: params?.amount,
        balanceGate,
        event: 'login',
        host,
        token: params?.token,
      })}
      title="Fund your account"
    >
      {deposit.actions}
    </Frame>
  )
}

declare namespace PostAuthorizeDeposit {
  type Props = {
    /** Balance-gate result that decided to show this prompt. */
    balanceGate: ShowDeposit.BalanceGate
    /** Chain used for the optional deposit prompt. */
    chainId: number
    /** Requesting app host. */
    host: string | undefined
    /** Sends the saved `wallet_authorizeAccessKey` result to the requesting app. */
    onContinue: () => void
    /** Completed `wallet_authorizeAccessKey` result waiting on the optional deposit prompt. */
    result: Result
    /** Requested post-authorization deposit capability. */
    showDeposit: ShowDeposit.Requested
  }
}
